import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { PostRettifica, Rettifica } from '../interfaces/rettifica.interface';
import { FiltroRettifiche } from '../interfaces/filtro-rettifiche.interface';
import { Attori } from '../shared/enum/attori.enum';

@Injectable({ providedIn: 'root' })
export class RettificheService {
  constructor(private readonly http: HttpClient) {}

  private readonly baseUrl = 'api/richiesteRettifica';

  public getRettifiche(): Observable<Rettifica[]> {
    return this.http.get<Rettifica[]>(this.baseUrl + '/richiesteRettifica');
  }

  public getRettificheDaRG(): Observable<Rettifica[]> {
    return this.http.get<Rettifica[]>(this.baseUrl + '/responsabile/risorsa');
  }

  public getRettificheDaR1L(): Observable<Rettifica[]> {
    return this.http.get<Rettifica[]>(
      this.baseUrl + '/responsabilePrimoLivello/risorsa'
    );
  }

  public postRettifica(rettifiche: Rettifica): Observable<PostRettifica> {
    const body = { ...rettifiche };
    return this.http.post<PostRettifica>(
      this.baseUrl + '/richiestaRettifica',
      body
    );
  }

  public deleteRettifica(rettificaId: number): Observable<any> {
    return this.http.delete<any>(
      this.baseUrl + '/deleteRichiestaRettifica/' + rettificaId
    );
  }

  public getElencoRettificheDaRcd(filter: FiltroRettifiche): Observable<any> {
    const responseType = filter.flagExport ? 'blob' : 'json';

    return this.http.post<FiltroRettifiche>(
      this.baseUrl + '/responsabileCommessa/ricerca',
      filter,
      { responseType: responseType as any }
    );
  }

  public approvaRifiutaRettifica(
    attore: number,
    rettifica: Rettifica
  ): Observable<any> {
    if (attore === Attori.ResponsabileCommessaDestinazione) {
      return this.http.patch<any>(
        this.baseUrl + '/responsabileCommessa/approvaRifiuta',
        { ...rettifica }
      );
    } else {
      return this.http.patch<any>(
        this.baseUrl + '/responsabile/approvaRifiuta',
        { ...rettifica }
      );
    }
  }

  public approvazioneMassiva(rettifiche: Rettifica[]): Observable<any> {
    return this.http.patch<any>(
      this.baseUrl + '/responsabile/approvazioneMassiva',
      rettifiche
    );
  }

  public getRichiesteRettificheByAttore(
    attore: number,
    filter: FiltroRettifiche
  ): Observable<any> {
    const responseType = filter.flagExport ? 'blob' : 'json';
    switch (attore) {
      case Attori.ResponsabileCommessaDestinazione:
        return this.http.post<FiltroRettifiche>(
          this.baseUrl + '/responsabileCommessa/ricerca',
          filter,
          {
            responseType: responseType as any,
          }
        );
      case Attori.ResponsabilePrimoLivello:
        return this.http.post<FiltroRettifiche>(
          this.baseUrl +
            `/responsabilePrimoLivello/${
              filter.richiestaPerAttore === Attori.ResponsabileGerarchico
                ? 'ricercaRichiesteRG'
                : 'ricercaRichiesteRCD'
            }`,
          filter,
          {
            responseType: responseType as any,
          }
        );
      case Attori.PianificazioneControllo:
        return this.http.post<FiltroRettifiche>(
          this.baseUrl + '/pianificazioneControllo/ricerca',
          filter,
          {
            responseType: responseType as any,
          }
        );
      case Attori.ControllerCentrale:
        return this.http.post<FiltroRettifiche>(
          this.baseUrl + '/controllerCentrale/ricerca',
          filter,
          {
            responseType: responseType as any,
          }
        );
      case Attori.ControllerBU:
        return this.http.post<FiltroRettifiche>(
          this.baseUrl + '/controllerBU/ricerca',
          filter,
          {
            responseType: responseType as any,
          }
        );
      case Attori.COO:
      case Attori.CFO:
      case Attori.DirettoreGenerale:
        return this.http.post<FiltroRettifiche>(
          this.baseUrl +
            `/coo/${
              filter.richiestaPerAttore === Attori.ResponsabileGerarchico
                ? 'rg'
                : 'rcd'
            }/ricerca`,
          filter,
          {
            responseType: responseType as any,
          }
        );
      default:
        return this.http.post<FiltroRettifiche>(
          this.baseUrl + '/responsabile/ricerca',
          filter,
          {
            responseType: responseType as any,
          }
        );
    }
  }
  public getRichiesteRettificheCountByAttore(
    attore: number,
    filter: FiltroRettifiche
  ): Observable<any> {
    switch (attore) {
      case Attori.ResponsabileCommessaDestinazione:
        return this.http.post<FiltroRettifiche>(
          this.baseUrl + '/responsabileCommessa/ricerca/count',
          filter
        );
      case Attori.ResponsabilePrimoLivello:
        return this.http.post<FiltroRettifiche>(
          this.baseUrl +
            `/responsabilePrimoLivello/${
              filter.richiestaPerAttore === Attori.ResponsabileGerarchico
                ? 'ricercaRichiesteRG/count'
                : 'ricercaRichiesteRCD/count'
            }`,
          filter
        );
      case Attori.PianificazioneControllo:
        return this.http.post<FiltroRettifiche>(
          this.baseUrl + '/pianificazioneControllo/ricerca/count',
          filter
        );
      case Attori.ControllerCentrale:
        return this.http.post<FiltroRettifiche>(
          this.baseUrl + '/controllerCentrale/ricerca/count',
          filter
        );
      case Attori.ControllerBU:
        return this.http.post<FiltroRettifiche>(
          this.baseUrl + '/controllerBU/ricerca/count',
          filter
        );
      case Attori.COO:
      case Attori.CFO:
      case Attori.DirettoreGenerale:
        return this.http.post<FiltroRettifiche>(
          this.baseUrl +
            `/coo/${
              filter.richiestaPerAttore === Attori.ResponsabileGerarchico
                ? 'rg'
                : 'rcd'
            }/ricerca/count`,
          filter
        );
      default:
        return this.http.post<FiltroRettifiche>(
          this.baseUrl + '/responsabile/ricerca/count',
          filter
        );
    }
  }

  getRettificheDaPianificazioneController(
    attore: Attori,
    idAnagraficaTimesheet: number,
    risorsa: string
  ): Observable<Rettifica[]> {
    switch (attore) {
      case Attori.PianificazioneControllo:
        return this.http.post<Rettifica[]>(
          this.baseUrl + '/pianificazioneControllo/risorsa',
          {
            idAnagraficaTimesheet: idAnagraficaTimesheet,
            matricola: risorsa,
          }
        );
      case Attori.ControllerCentrale:
        return this.http.post<Rettifica[]>(
          this.baseUrl + '/controllerCentrale/risorsa',
          {
            idAnagraficaTimesheet: idAnagraficaTimesheet,
            matricola: risorsa,
          }
        );
      case Attori.ControllerBU:
        return this.http.post<Rettifica[]>(
          this.baseUrl + '/controllerBU/risorsa',
          {
            idAnagraficaTimesheet: idAnagraficaTimesheet,
            matricola: risorsa,
          }
        );
      default:
        return of([]);
    }
  }

  public getModificheRisorsa(): Observable<Rettifica[]> {
    return this.http.get<Rettifica[]>('api/timesheetrev/rettifiche/risorsa');
  }

  public riepilogoRettifiche(
    filter: FiltroRettifiche,
    count: boolean = false
  ): Observable<any> {
    const baseUrl = 'api/timesheetrev';
    const responseType = filter.flagExport ? 'blob' : 'json';
    const endpoint = count
      ? baseUrl + '/rettifiche/riepilogo/count'
      : baseUrl + '/rettifiche/riepilogo';
    return this.http.post<FiltroRettifiche>(
      endpoint,
      filter,
      {
        responseType: responseType as any,
      });
  }

  public postModifica(modifica: Rettifica): Observable<PostRettifica> {
    const body = { ...modifica };
    return this.http.post<PostRettifica>(
      'api/timesheetrev/richiestaRettifica',
      body
    );
  }
  public getGiorniModificheRisorsa(): Observable<any> {
    return this.http.get<any>('api/timesheetrev/giorniModificati');
  }
}
